<template>
  <div>
    <Movementmenu />

    <v-container fluid class="pa-6" tag="section">
      <!-- ===== Page Header ===== -->
      <v-row class="mb-2">
        <v-col cols="12">
          <h2 class="text-h4 font-weight-bold mb-2 primary--text">
            <v-icon large color="primary" class="mr-2">mdi-chart-arc</v-icon>
            รายงานสรุปผลการพิจารณาย้ายข้าราชการครู
          </h2>
          <div class="subtitle-1 grey--text text--darken-1">
            บัญชีรายละเอียดแนบท้ายวาระการประชุมคณะกรรมการกลั่นกรองฯ ประจำรอบ
            {{ periods.period_times }}/{{ periods.period_year }}
            <span v-if="order_appoints.meeting_no">
              — ครั้งที่ {{ order_appoints.meeting_no }}
            </span>
          </div>
        </v-col>
      </v-row>

      <!-- ===== Summary Cards Row 1: ภาพรวมจำนวน ===== -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #455A64"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">ผู้ยื่นย้ายทั้งหมด</div>
                <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
              </div>
              <v-avatar color="info lighten-5" size="56">
                <v-icon color="info darken-3" x-large>mdi-account-group</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #2E7D32"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">ย้ายกรณีปกติ</div>
                <div class="text-h4 font-weight-bold success--text">
                  {{ stats.normalCount }}
                </div>
              </div>
              <v-avatar color="success lighten-5" size="56">
                <v-icon color="success darken-3" x-large
                  >mdi-swap-horizontal</v-icon
                >
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #A86400"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">ตัดโอน</div>
                <div
                  class="text-h4 font-weight-bold warning--text text--darken-3"
                >
                  {{ stats.cutCount }}
                </div>
              </div>
              <v-avatar color="warning lighten-5" size="56">
                <v-icon color="warning darken-3" x-large>mdi-transfer</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            :style="
              'border-left: 5px solid ' +
                (stats.errorCount > 0 ? '#C62828' : '#2E7D32')
            "
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">
                  ข้อมูลต้องตรวจสอบ
                </div>
                <div
                  class="text-h4 font-weight-bold"
                  :class="
                    stats.errorCount > 0
                      ? 'red--text text--darken-3'
                      : 'green--text text--darken-3'
                  "
                >
                  {{ stats.errorCount }}
                </div>
              </div>
              <v-avatar
                :color="
                  stats.errorCount > 0 ? 'error lighten-5' : 'success lighten-5'
                "
                size="56"
              >
                <v-icon
                  :color="
                    stats.errorCount > 0 ? 'error darken-3' : 'success darken-3'
                  "
                  x-large
                >
                  {{
                    stats.errorCount > 0
                      ? "mdi-alert-circle"
                      : "mdi-check-circle"
                  }}
                </v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Validation Panel ===== -->
      <v-card
        elevation="2"
        class="rounded-lg mb-4"
        :class="{
          'validation-card-error': errorList.length > 0,
          'validation-card-ok': errorList.length === 0
        }"
      >
        <v-toolbar flat dense>
          <v-icon :color="errorList.length > 0 ? 'error' : 'success'" class="mr-2">
            {{ errorList.length > 0 ? "mdi-shield-alert" : "mdi-shield-check" }}
          </v-icon>
          <v-toolbar-title class="text-subtitle-1 font-weight-bold">
            ผลการตรวจสอบข้อมูล
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip-group v-model="validationFilter" mandatory>
            <v-chip filter outlined small value="all">
              ทั้งหมด ({{ stats.total }})
            </v-chip>
            <v-chip
              filter
              outlined
              small
              value="doc_error"
              :color="stats.docError > 0 ? 'error' : 'grey'"
            >
              <v-icon x-small left>mdi-file-alert</v-icon>
              เอกสาร ({{ stats.docError }})
            </v-chip>
            <v-chip
              filter
              outlined
              small
              value="no_opinion"
              :color="stats.noOpinion > 0 ? 'warning' : 'grey'"
            >
              <v-icon x-small left>mdi-account-question</v-icon>
              ไม่มีความเห็น ({{ stats.noOpinion }})
            </v-chip>
            <v-chip
              filter
              outlined
              small
              value="no_score"
              :color="stats.noScore > 0 ? 'primary' : 'grey'"
            >
              <v-icon x-small left>mdi-calculator-variant</v-icon>
              ไม่มีคะแนน ({{ stats.noScore }})
            </v-chip>
          </v-chip-group>
        </v-toolbar>

        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-alert
                :type="stats.docError > 0 ? 'error' : 'success'"
                dense
                text
                class="mb-0"
              >
                <strong>เอกสารไม่สมบูรณ์/ไม่ส่ง:</strong>
                {{ stats.docError }} รายการ
              </v-alert>
            </v-col>
            <v-col cols="12" sm="3">
              <v-alert
                :type="stats.noOpinion > 0 ? 'warning' : 'success'"
                dense
                text
                class="mb-0"
              >
                <strong>ผอ.ยังไม่ให้ความเห็น:</strong>
                {{ stats.noOpinion }} รายการ
              </v-alert>
            </v-col>
            <v-col cols="12" sm="3">
              <v-alert
                :type="stats.noScore > 0 ? 'warning' : 'success'"
                dense
                text
                class="mb-0"
              >
                <strong>ยังไม่มีคะแนน:</strong>
                {{ stats.noScore }} รายการ
              </v-alert>
            </v-col>
            <v-col cols="12" sm="3">
              <v-alert type="info" dense text class="mb-0">
                <strong>ผ่านตรวจสอบ:</strong>
                {{ stats.total - stats.errorCount }} รายการ
              </v-alert>
            </v-col>
          </v-row>

          <!-- Error Detail Table -->
          <v-expand-transition>
            <div
              v-if="validationFilter !== 'all' && filteredErrors.length > 0"
              class="mt-3"
            >
              <v-data-table
                :headers="errorHeaders"
                :items="filteredErrors"
                :items-per-page="10"
                dense
                class="elevation-0"
              >
                <template v-slot:item.severity="{ item }">
                  <v-chip
                    :color="item.severity === 'error' ? 'error' : 'warning'"
                    dark
                    x-small
                    label
                  >
                    {{ item.severity === "error" ? "ผิดพลาด" : "เตือน" }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <!-- ===== Statistics Row 1: เอกสาร / ผอ. / รูปแบบ ===== -->
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="info darken-1"
                >mdi-file-document-check</v-icon
              >
              สถานะเอกสาร
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div
                v-for="(item, key) in docStatusSummary"
                :key="key"
                class="mb-3"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-chip :color="item.color" dark small>{{
                    item.label
                  }}</v-chip>
                  <span class="font-weight-bold"
                    >{{ item.count }} ราย ({{ item.percent }}%)</span
                  >
                </div>
                <v-progress-linear
                  :value="item.percent"
                  :color="item.color"
                  height="8"
                  rounded
                ></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="primary darken-1">mdi-account-check</v-icon>
              ความคิดเห็น ผอ.
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div
                v-for="(item, key) in directorOpinionSummary"
                :key="key"
                class="mb-3"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-chip :color="item.color" dark small>{{
                    item.label
                  }}</v-chip>
                  <span class="font-weight-bold"
                    >{{ item.count }} ราย ({{ item.percent }}%)</span
                  >
                </div>
                <v-progress-linear
                  :value="item.percent"
                  :color="item.color"
                  height="8"
                  rounded
                ></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="info darken-1">mdi-swap-vertical</v-icon>
              รูปแบบการย้าย
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div
                v-for="(item, key) in transferTypeSummary"
                :key="key"
                class="mb-3"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-chip :color="item.color" dark small>{{
                    item.label
                  }}</v-chip>
                  <span class="font-weight-bold"
                    >{{ item.count }} ราย ({{ item.percent }}%)</span
                  >
                </div>
                <v-progress-linear
                  :value="item.percent"
                  :color="item.color"
                  height="8"
                  rounded
                ></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Statistics Row 2: เหตุผล / คะแนน ===== -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="primary darken-1"
                >mdi-clipboard-list</v-icon
              >
              เหตุผลในการย้าย
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>เหตุผล</th>
                    <th class="text-center">จำนวน</th>
                    <th class="text-center">สัดส่วน</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in reasonSummary" :key="idx">
                    <td>{{ item.label }}</td>
                    <td class="text-center font-weight-bold">
                      {{ item.count }}
                    </td>
                    <td class="text-center">{{ item.percent }}%</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="warning darken-3">mdi-chart-line</v-icon>
              สถิติคะแนน
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>รายการ</th>
                    <th class="text-center">ย้ายปกติ</th>
                    <th class="text-center">ตัดโอน</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>คะแนนเฉลี่ย สอจ.</td>
                    <td class="text-center font-weight-bold">
                      {{ scoreStats.normal.avgProvince }}
                    </td>
                    <td class="text-center font-weight-bold">
                      {{ scoreStats.cut.avgProvince }}
                    </td>
                  </tr>
                  <tr>
                    <td>คะแนนเฉลี่ย กก.</td>
                    <td class="text-center font-weight-bold">
                      {{ scoreStats.normal.avgCommittee }}
                    </td>
                    <td class="text-center font-weight-bold">
                      {{ scoreStats.cut.avgCommittee }}
                    </td>
                  </tr>
                  <tr>
                    <td>คะแนนรวมเฉลี่ย</td>
                    <td class="text-center font-weight-bold primary--text">
                      {{ scoreStats.normal.avgTotal }}
                    </td>
                    <td class="text-center font-weight-bold primary--text">
                      {{ scoreStats.cut.avgTotal }}
                    </td>
                  </tr>
                  <tr>
                    <td>คะแนนรวมสูงสุด</td>
                    <td class="text-center font-weight-bold success--text">
                      {{ scoreStats.normal.maxTotal }}
                    </td>
                    <td class="text-center font-weight-bold success--text">
                      {{ scoreStats.cut.maxTotal }}
                    </td>
                  </tr>
                  <tr>
                    <td>คะแนนรวมต่ำสุด</td>
                    <td class="text-center font-weight-bold error--text">
                      {{ scoreStats.normal.minTotal }}
                    </td>
                    <td class="text-center font-weight-bold error--text">
                      {{ scoreStats.cut.minTotal }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Statistics Row 3: ช่วยราชการ / แข่งขัน / ความเห็น ===== -->
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="info darken-2">mdi-account-switch</v-icon>
              สถานะช่วยราชการ
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip color="success" dark small>ปกติ</v-chip>
                <span class="font-weight-bold text-h6"
                  >{{ serviceStatusSummary.normal }} ราย</span
                >
              </div>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip color="warning" dark small>ช่วยราชการ</v-chip>
                <span class="font-weight-bold text-h6"
                  >{{ serviceStatusSummary.service }} ราย</span
                >
              </div>
              <v-divider class="my-2"></v-divider>
              <v-progress-linear
                :value="serviceStatusSummary.normalPercent"
                color="success"
                background-color="warning lighten-3"
                height="20"
                rounded
              >
                <template v-slot:default>
                  <span class="white--text caption"
                    >ปกติ {{ serviceStatusSummary.normalPercent }}%</span
                  >
                </template>
              </v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="error darken-1">mdi-account-multiple</v-icon>
              การแข่งขัน / อันดับคะแนน
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-2">
                <span>ตำแหน่งที่มีผู้แข่งขัน</span>
                <span class="font-weight-bold text-h6 error--text">{{
                  competitionSummary.contested
                }}</span>
              </div>
              <div class="d-flex align-center justify-space-between mb-2">
                <span>ตำแหน่งไม่มีผู้แข่งขัน</span>
                <span class="font-weight-bold text-h6 success--text">{{
                  competitionSummary.uncontested
                }}</span>
              </div>
              <div class="d-flex align-center justify-space-between mb-3">
                <span>ผู้แข่งขันสูงสุด/ตำแหน่ง</span>
                <span class="font-weight-bold text-h6 warning--text">{{
                  competitionSummary.maxCompetitors
                }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex align-center justify-space-between mt-2">
                <v-chip color="info" dark small>ยื่นรายเดียว</v-chip>
                <span class="font-weight-bold"
                  >{{ remarkSummary.single }} ราย</span
                >
              </div>
              <div class="d-flex align-center justify-space-between mt-2">
                <v-chip color="success" dark small>อันดับ 1</v-chip>
                <span class="font-weight-bold"
                  >{{ remarkSummary.rank1 }} ราย</span
                >
              </div>
              <div class="d-flex align-center justify-space-between mt-2">
                <v-chip color="warning" dark small>อันดับอื่นๆ</v-chip>
                <span class="font-weight-bold"
                  >{{ remarkSummary.other }} ราย</span
                >
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="primary darken-1">mdi-map-marker-check</v-icon>
              ความเห็นกรรมการ / ผอ.สถบ.
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-caption font-weight-bold grey--text mb-2">
                ปลายทาง (กรรมการ)
              </div>
              <div
                v-for="(item, key) in mCommitteeSummary"
                :key="'mc' + key"
                class="mb-2"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-chip :color="item.color" dark x-small>{{
                    item.label
                  }}</v-chip>
                  <span class="caption font-weight-bold"
                    >{{ item.count }} ({{ item.percent }}%)</span
                  >
                </div>
                <v-progress-linear
                  :value="item.percent"
                  :color="item.color"
                  height="5"
                  rounded
                ></v-progress-linear>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="text-caption font-weight-bold grey--text mb-2">
                ผอ.สถบ.
              </div>
              <div
                v-for="(item, key) in stbOpinionSummary"
                :key="'stb' + key"
                class="mb-2"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <v-chip :color="item.color" dark x-small>{{
                    item.label
                  }}</v-chip>
                  <span class="caption font-weight-bold"
                    >{{ item.count }} ({{ item.percent }}%)</span
                  >
                </div>
                <v-progress-linear
                  :value="item.percent"
                  :color="item.color"
                  height="5"
                  rounded
                ></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Top Rankings ===== -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="success darken-2">mdi-school</v-icon>
              สถานศึกษาปลายทางยอดนิยม (Top 10)
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th width="50">อันดับ</th>
                    <th>สถานศึกษา</th>
                    <th class="text-center" width="80">จำนวน</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in topDestinations" :key="idx">
                    <td>
                      <v-chip
                        v-if="idx < 3"
                        :color="
                          idx === 0
                            ? 'warning darken-2'
                            : idx === 1
                            ? 'grey'
                            : 'secondary lighten-1'
                        "
                        dark
                        x-small
                        label
                        >{{ idx + 1 }}</v-chip
                      >
                      <span v-else>{{ idx + 1 }}</span>
                    </td>
                    <td>{{ item.name }}</td>
                    <td class="text-center font-weight-bold">
                      {{ item.count }}
                    </td>
                  </tr>
                  <tr v-if="topDestinations.length === 0">
                    <td colspan="3" class="text-center grey--text">
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="rounded-lg elevation-2 fill-height">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              <v-icon left color="info darken-2">mdi-book-education</v-icon>
              สาขาวิชาที่ขอย้ายมากที่สุด (Top 10)
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th width="50">อันดับ</th>
                    <th>สาขาวิชา</th>
                    <th class="text-center" width="80">จำนวน</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in topBranches" :key="idx">
                    <td>
                      <v-chip
                        v-if="idx < 3"
                        :color="
                          idx === 0
                            ? 'warning darken-2'
                            : idx === 1
                            ? 'grey'
                            : 'secondary lighten-1'
                        "
                        dark
                        x-small
                        label
                        >{{ idx + 1 }}</v-chip
                      >
                      <span v-else>{{ idx + 1 }}</span>
                    </td>
                    <td>{{ item.name }}</td>
                    <td class="text-center font-weight-bold">
                      {{ item.count }}
                    </td>
                  </tr>
                  <tr v-if="topBranches.length === 0">
                    <td colspan="3" class="text-center grey--text">
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== Toolbar: ค้นหา / สลับ / Export ===== -->
      <v-card elevation="2" class="rounded-lg mb-4">
        <v-toolbar flat color="white" class="pt-2">
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="ค้นหา ชื่อ, วิทยาลัย, สาขา..."
                single-line
                hide-details
                outlined
                dense
                rounded
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="tableView"
                :items="tableViewOptions"
                label="แสดงข้อมูล"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="success"
                outlined
                block
                rounded
                @click="exportToExcel"
              >
                <v-icon left>mdi-file-excel</v-icon> Export Excel
              </v-btn>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn color="primary" outlined block rounded target="_blank"  :href="getReportScoreLink()" >
                <v-icon left>mdi-printer</v-icon> พิมพ์คะแนนจากกรรมการกลั่นกรอง
              </v-btn>
            </v-col>

            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                outlined
                block
                rounded
                :href="getReportLink()"
                target="_blank"
              >
                <v-icon left>mdi-printer</v-icon> พิมพ์รายงาน
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>

        <!-- Column Customizer -->
        <v-expansion-panels flat accordion class="mt-2">
          <v-expansion-panel>
            <v-expansion-panel-header class="grey lighten-4">
              <span class="text-subtitle-2 grey--text text--darken-2">
                <v-icon small class="mr-2">mdi-table-cog</v-icon>
                ปรับแต่งคอลัมน์ตาราง
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-4">
              <v-select
                v-model="selectedHeaders"
                :items="headersMap"
                label="เลือกคอลัมน์ที่ต้องการแสดง"
                multiple
                outlined
                dense
                chips
                small-chips
                deletable-chips
                return-object
              ></v-select>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <!-- ===== Data Table ===== -->
      <v-card elevation="2" class="rounded-lg">
        <v-card-title class="text-subtitle-1">
          <v-icon left>mdi-table</v-icon>
          {{ tableViewLabel }}
          ({{ currentTableData.length }} ราย)
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          :headers="showHeaders"
          :items="currentTableData"
          :search="search"
          :loading="loading"
          :item-class="row_classes"
          class="elevation-0"
          :items-per-page="10"
        >
          <!-- Validation Status -->
          <template v-slot:item.validation_status="{ item }">
            <div class="d-flex align-center justify-center">
              <v-tooltip
                bottom
                v-if="
                  item.status_document === 'do_not' ||
                    item.status_document === 'incomplete'
                "
              >
                <template v-slot:activator="{ on }">
                  <v-chip v-on="on" color="error" dark x-small label class="mr-1">
                    <v-icon x-small left>mdi-file-alert</v-icon>เอกสาร
                  </v-chip>
                </template>
                <span>{{
                  item.status_document === "do_not"
                    ? "ไม่จัดส่งเอกสาร"
                    : "เอกสารไม่สมบูรณ์"
                }}</span>
              </v-tooltip>
              <v-tooltip
                bottom
                v-if="
                  !item.comment_dr_c ||
                    (item.comment_dr_c !== 'approp' &&
                      item.comment_dr_c !== 'inapprop')
                "
              >
                <template v-slot:activator="{ on }">
                  <v-chip
                    v-on="on"
                    color="warning"
                    dark
                    x-small
                    label
                    class="mr-1"
                  >
                    <v-icon x-small left>mdi-account-question</v-icon>ผอ.
                  </v-chip>
                </template>
                <span>ผอ. ยังไม่ให้ความคิดเห็น</span>
              </v-tooltip>
              <v-icon v-if="!hasIssue(item)" color="success" small
                >mdi-check-circle</v-icon
              >
            </div>
          </template>

          <!-- Total Score -->
          <template v-slot:item.totalScore="{ item }">
            <span class="font-weight-bold">{{
              calcTotalScore(item).toFixed(2)
            }}</span>
          </template>

          <!-- Document Status -->
          <template v-slot:item.status_document="{ item }">
            <v-chip :color="getDocColor(item.status_document)" dark x-small>
              {{ getDocLabel(item.status_document) }}
            </v-chip>
          </template>

          <!-- Director Opinion -->
          <template v-slot:item.comment_dr_c="{ item }">
            <v-chip :color="getOpinionColor(item.comment_dr_c)" dark x-small>
              {{ getOpinionLabel(item.comment_dr_c) }}
            </v-chip>
          </template>

          <!-- STB Opinion -->
          <template v-slot:item.tp_comment_dr_stb="{ item }">
            <v-chip
              :color="getOpinionColor(item.tp_comment_dr_stb)"
              dark
              x-small
            >
              {{ getOpinionLabel(item.tp_comment_dr_stb) }}
            </v-chip>
          </template>

          <!-- Committee Opinion -->
          <template v-slot:item.Mcommittee="{ item }">
            <v-chip :color="getOpinionColor(item.Mcommittee)" dark x-small>
              {{ getOpinionLabel(item.Mcommittee) }}
            </v-chip>
          </template>

          <!-- Transfer Type -->
          <template v-slot:item.ctsstatus_select="{ item }">
            <v-chip
              :color="getStatusColor(item.ctsstatus_select)"
              dark
              small
              label
            >
              {{ getStatusText(item.ctsstatus_select) }}
            </v-chip>
          </template>

          <!-- Service Status -->
          <template v-slot:item.service_statuss="{ item }">
            <v-chip
              :color="
                item.service_statuss === 'not_service' ? 'success' : 'warning'
              "
              dark
              x-small
            >
              {{
                item.service_statuss === "not_service" ? "ปกติ" : "ช่วยราชการ"
              }}
            </v-chip>
          </template>

          <!-- Remark -->
          <template v-slot:item.remark="{ item }">
            <v-chip :color="getRemarkObj(item).color" dark x-small>
              {{ getRemarkObj(item).text }}
            </v-chip>
          </template>

          <!-- College New -->
          <template v-slot:item.college_name_new="{ item }">
            <div class="d-flex align-center">
              <v-icon color="success" small class="mr-1"
                >mdi-map-marker-right</v-icon
              >
              <span class="success--text font-weight-bold">{{
                item.college_name_new
              }}</span>
            </div>
          </template>

          <!-- College Old -->
          <template v-slot:item.college_name_old="{ item }">
            <div class="grey--text text--darken-2">
              {{ item.college_name_old }}
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- ===== Snackbar ===== -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2" dark>{{ snackbar.icon }}</v-icon>
        <span class="white--text">{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text dark v-bind="attrs" @click="snackbar.show = false"
          >ปิด</v-btn
        >
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Movementmenu from "../../components/admin/movementmenu.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default {
  name: "ReportMovementFilter",
  components: { Movementmenu },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      search: "",

      // View Toggle
      tableView: "all",
      tableViewOptions: [
        { text: "ทั้งหมด", value: "all" },
        { text: "ย้ายปกติ", value: "normal" },
        { text: "ตัดโอน", value: "cut" }
      ],

      // Validation
      validationFilter: "all",

      // Data
      order_appoints: {},
      periods: {},
      conditons_transfer_successs: [],
      conditons_transfer_successsCut: [],

      // Error Headers
      errorHeaders: [
        { text: "ระดับ", value: "severity", width: "80px" },
        { text: "ชื่อ-สกุล", value: "name" },
        { text: "สังกัดเดิม", value: "college" },
        { text: "ปัญหาที่พบ", value: "issue" }
      ],

      // Table Headers
      selectedHeaders: [],
      headersMap: [
        {
          text: "ตรวจสอบ",
          align: "center",
          value: "validation_status",
          width: "120px",
          sortable: false
        },
        { text: "รูปแบบ", align: "center", value: "ctsstatus_select" },
        { text: "สังกัดใหม่", align: "left", value: "college_name_new" },
        { text: "คำนำหน้า", align: "left", value: "title_s" },
        { text: "ชื่อ", align: "left", value: "frist_name" },
        { text: "นามสกุล", align: "left", value: "last_name" },
        { text: "สังกัดเดิม", align: "left", value: "college_name_old" },
        { text: "สาขา", align: "left", value: "branch_name" },
        {
          text: "คะแนน สอจ.",
          align: "center",
          value: "tpvecprovince_score"
        },
        { text: "คะแนน กก.", align: "center", value: "score_1420" },
        { text: "รวม", align: "center", value: "totalScore" },
        { text: "เอกสาร", align: "center", value: "status_document" },
        { text: "ผอ.", align: "center", value: "comment_dr_c" },
        { text: "ผอ.สถบ.", align: "center", value: "tp_comment_dr_stb" },
        { text: "กรรมการ", align: "center", value: "Mcommittee" },
        { text: "ช่วยราชการ", align: "center", value: "service_statuss" },
        { text: "เหตุผล", align: "left", value: "reason" },
        { text: "อายุงาน", align: "center", value: "age_app_time" },
        { text: "อายุราชการ", align: "center", value: "age_time" },
        { text: "หมายเหตุ", align: "center", value: "remark" }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: ""
      }
    };
  },

  computed: {
    allData() {
      return [
        ...this.conditons_transfer_successs,
        ...this.conditons_transfer_successsCut
      ];
    },

    showHeaders() {
      return this.headersMap.filter(h => this.selectedHeaders.includes(h));
    },

    currentTableData() {
      if (this.tableView === "normal") return this.conditons_transfer_successs;
      if (this.tableView === "cut") return this.conditons_transfer_successsCut;
      return this.allData;
    },

    tableViewLabel() {
      if (this.tableView === "normal") return "ข้อมูลย้ายกรณีปกติ";
      if (this.tableView === "cut") return "ข้อมูลย้ายตัดโอน";
      return "ข้อมูลทั้งหมด";
    },

    // ==========================================
    // STATS
    // ==========================================
    stats() {
      const all = this.allData;
      const total = all.length;
      const normalCount = this.conditons_transfer_successs.length;
      const cutCount = this.conditons_transfer_successsCut.length;

      const docError = all.filter(
        i =>
          i.status_document === "do_not" || i.status_document === "incomplete"
      ).length;

      const noOpinion = all.filter(
        i =>
          !i.comment_dr_c ||
          (i.comment_dr_c !== "approp" && i.comment_dr_c !== "inapprop")
      ).length;

      const noScore = all.filter(i => !i.tpvecprovince_score && !i.score_1420)
        .length;

      const errorSet = new Set();
      all.forEach((item, idx) => {
        if (
          item.status_document === "do_not" ||
          item.status_document === "incomplete"
        )
          errorSet.add(idx);
        if (
          !item.comment_dr_c ||
          (item.comment_dr_c !== "approp" && item.comment_dr_c !== "inapprop")
        )
          errorSet.add(idx);
        if (!item.tpvecprovince_score && !item.score_1420) errorSet.add(idx);
        if (!item.title_s) errorSet.add(idx);
      });

      return {
        total,
        normalCount,
        cutCount,
        docError,
        noOpinion,
        noScore,
        errorCount: errorSet.size
      };
    },

    // ==========================================
    // ERROR LIST
    // ==========================================
    errorList() {
      const errors = [];
      this.allData.forEach(item => {
        if (
          item.status_document === "do_not" ||
          item.status_document === "incomplete"
        ) {
          errors.push({
            severity: item.status_document === "do_not" ? "error" : "warning",
            name: item.title_s || item.ctsid_card || "-",
            college: item.college_name_old || "-",
            issue:
              item.status_document === "do_not"
                ? "ไม่จัดส่งเอกสาร"
                : "เอกสารไม่สมบูรณ์",
            type: "doc_error"
          });
        }
        if (
          !item.comment_dr_c ||
          (item.comment_dr_c !== "approp" && item.comment_dr_c !== "inapprop")
        ) {
          errors.push({
            severity: "warning",
            name: item.title_s || item.ctsid_card || "-",
            college: item.college_name_old || "-",
            issue: "ผอ. ยังไม่ให้ความคิดเห็น",
            type: "no_opinion"
          });
        }
        if (!item.tpvecprovince_score && !item.score_1420) {
          errors.push({
            severity: "warning",
            name: item.title_s || item.ctsid_card || "-",
            college: item.college_name_old || "-",
            issue: "ยังไม่มีข้อมูลคะแนน",
            type: "no_score"
          });
        }
        if (!item.title_s) {
          errors.push({
            severity: "error",
            name: item.ctsid_card || "ไม่ทราบ",
            college: item.college_name_old || "-",
            issue: "ไม่พบข้อมูลชื่อ-สกุล",
            type: "doc_error"
          });
        }
      });
      return errors;
    },

    filteredErrors() {
      if (this.validationFilter === "all") return this.errorList;
      return this.errorList.filter(e => e.type === this.validationFilter);
    },

    // ==========================================
    // DOCUMENT STATUS
    // ==========================================
    docStatusSummary() {
      const all = this.allData;
      const total = all.length || 1;
      const complete = all.filter(i => i.status_document === "complete").length;
      const incomplete = all.filter(i => i.status_document === "incomplete")
        .length;
      const doNot = all.filter(i => i.status_document === "do_not").length;
      const noData = total - complete - incomplete - doNot;
      return {
        complete: {
          label: "สมบูรณ์",
          count: complete,
          percent: ((complete / total) * 100).toFixed(1),
          color: "green"
        },
        incomplete: {
          label: "ไม่สมบูรณ์",
          count: incomplete,
          percent: ((incomplete / total) * 100).toFixed(1),
          color: "orange"
        },
        do_not: {
          label: "ไม่จัดส่ง",
          count: doNot,
          percent: ((doNot / total) * 100).toFixed(1),
          color: "red"
        },
        none: {
          label: "ไม่มีข้อมูล",
          count: noData,
          percent: ((noData / total) * 100).toFixed(1),
          color: "grey"
        }
      };
    },

    // ==========================================
    // OPINIONS
    // ==========================================
    directorOpinionSummary() {
      return this.buildOpinionSummary("comment_dr_c");
    },
    stbOpinionSummary() {
      return this.buildOpinionSummary("tp_comment_dr_stb");
    },
    mCommitteeSummary() {
      return this.buildOpinionSummary("Mcommittee");
    },

    // ==========================================
    // TRANSFER TYPE
    // ==========================================
    transferTypeSummary() {
      const all = this.allData;
      const total = all.length || 1;
      const demand = all.filter(
        i => i.ctsstatus_select === "demand" || i.ctsstatus_select === "agree"
      ).length;
      const transfer = all.filter(i => i.ctsstatus_select === "transfer")
        .length;
      const swNormal = all.filter(i => i.ctsstatus_select === "sw_normal")
        .length;
      const swAgree = all.filter(i => i.ctsstatus_select === "sw_agree").length;
      return {
        normal: {
          label: "ปกติ",
          count: demand,
          percent: ((demand / total) * 100).toFixed(1),
          color: "blue"
        },
        transfer: {
          label: "ตัดโอน",
          count: transfer,
          percent: ((transfer / total) * 100).toFixed(1),
          color: "teal"
        },
        sw_normal: {
          label: "สับเปลี่ยน",
          count: swNormal,
          percent: ((swNormal / total) * 100).toFixed(1),
          color: "primary"
        },
        sw_agree: {
          label: "แลกเปลี่ยน",
          count: swAgree,
          percent: ((swAgree / total) * 100).toFixed(1),
          color: "brown"
        }
      };
    },

    // ==========================================
    // REASONS
    // ==========================================
    reasonSummary() {
      const all = this.allData;
      const total = all.length || 1;
      const reasons = [
        { label: "(1) ย้ายเพื่ออยู่ร่วมคู่สมรส", count: 0 },
        { label: "(2) ย้ายเพื่อดูแลบิดา มารดา", count: 0 },
        { label: "(3) ย้ายเพื่อกลับภูมิลำเนา", count: 0 },
        { label: "(4) เหตุผลอื่น", count: 0 }
      ];
      all.forEach(item => {
        const r = item.reason || "";
        if (r.includes("(1)")) reasons[0].count++;
        if (r.includes("(2)")) reasons[1].count++;
        if (r.includes("(3)")) reasons[2].count++;
        if (r.includes("(4)")) reasons[3].count++;
      });
      return reasons.map(r => ({
        ...r,
        percent: ((r.count / total) * 100).toFixed(1)
      }));
    },

    // ==========================================
    // SCORES
    // ==========================================
    scoreStats() {
      return {
        normal: this.calcScoreStats(this.conditons_transfer_successs),
        cut: this.calcScoreStats(this.conditons_transfer_successsCut)
      };
    },

    // ==========================================
    // SERVICE STATUS
    // ==========================================
    serviceStatusSummary() {
      const all = this.allData;
      const total = all.length || 1;
      const normal = all.filter(
        i =>
          i.service_statuss === "not_service" ||
          i.service_status === "not_service"
      ).length;
      const service = total - normal;
      return {
        normal,
        service,
        normalPercent: ((normal / total) * 100).toFixed(1)
      };
    },

    // ==========================================
    // COMPETITION
    // ==========================================
    competitionSummary() {
      const all = this.allData;
      let contested = 0;
      let uncontested = 0;
      let maxCompetitors = 0;
      all.forEach(item => {
        const locs = (item.transference_locations || []).length;
        if (locs > 0) {
          contested++;
          maxCompetitors = Math.max(maxCompetitors, locs + 1);
        } else {
          uncontested++;
        }
      });
      return { contested, uncontested, maxCompetitors };
    },

    // ==========================================
    // REMARK SUMMARY
    // ==========================================
    remarkSummary() {
      const all = this.allData;
      let single = 0,
        rank1 = 0,
        other = 0;
      all.forEach(item => {
        const r = this.getRemarkObj(item);
        if (r.text === "ยื่นย้ายรายเดียว") single++;
        else if (r.text === "คะแนนอันดับ 1") rank1++;
        else other++;
      });
      return { single, rank1, other };
    },

    // ==========================================
    // TOP N
    // ==========================================
    topDestinations() {
      return this.getTopN(this.allData, "college_name_new", 10);
    },
    topBranches() {
      return this.getTopN(this.allData, "branch_name", 10);
    }
  },

  created() {
    const defaultKeys = [
      "validation_status",
      "ctsstatus_select",
      "college_name_new",
      "title_s",
      "frist_name",
      "last_name",
      "college_name_old",
      "branch_name",
      "tpvecprovince_score",
      "score_1420",
      "totalScore",
      "status_document",
      "comment_dr_c",
      "remark"
    ];
    this.selectedHeaders = this.headersMap.filter(h =>
      defaultKeys.includes(h.value)
    );
  },

  async mounted() {
    this.loading = true;
    await this.periodQuery();
    await this.order_appointQueryAll();
    await Promise.all([
      this.conditons_transfer_successQueryAll(),
      this.conditons_transfer_successCutQueryAll()
    ]);
    this.loading = false;

    this.$nextTick(() => {
      if (this.stats.errorCount > 0) {
        this.showSnackbar(
          "warning",
          "พบ " + this.stats.errorCount + " รายการที่ต้องตรวจสอบ"
        );
      } else {
        this.showSnackbar(
          "success",
          "โหลดข้อมูลเรียบร้อย " + this.stats.total + " รายการ"
        );
      }
    });
  },

  methods: {
    getReportLink() {
      // Construct link based on pattern in original code
      return `#/admin/print_report_movement_filter_o/${this.order_appoints[0].id_oa}/${this.order_appoints[0].time_s}/${this.order_appoints[0].year_s}/teach`;
    },
    getReportScoreLink() {
      return `#/admin/print_report_movement_filter_3_score/${this.order_appoints[0].id_oa}/${this.order_appoints[0].time_s}/${this.order_appoints[0].year_s}/teach`;
    },
    // ==========================================
    // DATA FETCHING
    // ==========================================
    async periodQuery() {
      try {
        let result = await this.$http.post("period.php", {
          ApiKey: this.ApiKey,
          period_id: "301"
        });
        this.periods = result.data;
      } catch (e) {
        console.error("Period Error", e);
      }
    },

    async order_appointQueryAll() {
      try {
        let result = await this.$http.post("order_appoint.php", {
          ApiKey: this.ApiKey,
          id_oa: this.id_url
        });
        this.order_appoints = result.data;
      } catch (e) {
        console.error("order_appoint error:", e);
      }
    },

    async conditons_transfer_successQueryAll() {
      try {
        let result = await this.$http.post(
          "conditons_transfer_success_filter_3.php",
          {
            ApiKey: this.ApiKey,
            time_s: this.periods.period_times,
            year_s: this.periods.period_year,
            name_position: "ครู",
            ctsstatus_selectfrist: "ok"
          }
        );
        this.conditons_transfer_successs = Array.isArray(result.data)
          ? result.data
          : [];
      } catch (e) {
        console.error("normal query error:", e);
        this.conditons_transfer_successs = [];
      }
    },

    async conditons_transfer_successCutQueryAll() {
      try {
        let result = await this.$http.post(
          "conditons_transfer_success_filter_3.php",
          {
            ApiKey: this.ApiKey,
            time_s: this.periods.period_times,
            year_s: this.periods.period_year,
            name_position: "ครู",
            ctsstatus_selectsecord: "transfer"
          }
        );
        this.conditons_transfer_successsCut = Array.isArray(result.data)
          ? result.data
          : [];
      } catch (e) {
        console.error("cut query error:", e);
        this.conditons_transfer_successsCut = [];
      }
    },

    // ==========================================
    // SCORE / REMARK HELPERS
    // ==========================================
    calcTotalScore(person) {
      return (
        parseFloat(person.tpvecprovince_score || 0) +
        parseFloat(person.score_1420 || 0)
      );
    },

    calcScoreStats(data) {
      if (!data || data.length === 0) {
        return {
          avgProvince: "0.00",
          avgCommittee: "0.00",
          avgTotal: "0.00",
          maxTotal: "0.00",
          minTotal: "0.00"
        };
      }
      let sumP = 0,
        sumC = 0,
        maxT = -Infinity,
        minT = Infinity;
      data.forEach(item => {
        const p = parseFloat(item.tpvecprovince_score || 0);
        const c = parseFloat(item.score_1420 || 0);
        const t = p + c;
        sumP += p;
        sumC += c;
        if (t > maxT) maxT = t;
        if (t < minT) minT = t;
      });
      const n = data.length;
      return {
        avgProvince: (sumP / n).toFixed(2),
        avgCommittee: (sumC / n).toFixed(2),
        avgTotal: ((sumP + sumC) / n).toFixed(2),
        maxTotal: maxT === -Infinity ? "0.00" : maxT.toFixed(2),
        minTotal: minT === Infinity ? "0.00" : minT.toFixed(2)
      };
    },

    getRemarkObj(item) {
      const competitors = item.transference_locations || [];
      if (competitors.length === 0) {
        return { text: "ยื่นย้ายรายเดียว", color: "blue" };
      }
      const myScore = this.calcTotalScore(item);
      const higherCount = competitors.filter(
        c => this.calcTotalScore(c) > myScore
      ).length;
      const rank = 1 + higherCount;
      return {
        text: "คะแนนอันดับ " + rank,
        color: rank === 1 ? "green" : "orange"
      };
    },

    // ==========================================
    // VALIDATION HELPERS
    // ==========================================
    hasIssue(item) {
      if (
        item.status_document === "do_not" ||
        item.status_document === "incomplete"
      )
        return true;
      if (
        !item.comment_dr_c ||
        (item.comment_dr_c !== "approp" && item.comment_dr_c !== "inapprop")
      )
        return true;
      if (!item.tpvecprovince_score && !item.score_1420) return true;
      if (!item.title_s) return true;
      return false;
    },

    row_classes(item) {
      if (
        item.status_document === "do_not" ||
        item.status_document === "incomplete"
      )
        return "error-row";
      if (
        !item.comment_dr_c ||
        (item.comment_dr_c !== "approp" && item.comment_dr_c !== "inapprop")
      )
        return "warning-row";
      return "";
    },

    // ==========================================
    // SUMMARY BUILDER
    // ==========================================
    buildOpinionSummary(field) {
      const all = this.allData;
      const total = all.length || 1;
      const approp = all.filter(i => i[field] === "approp").length;
      const inapprop = all.filter(i => i[field] === "inapprop").length;
      const none = total - approp - inapprop;
      return {
        approp: {
          label: "เห็นควร",
          count: approp,
          percent: ((approp / total) * 100).toFixed(1),
          color: "green"
        },
        inapprop: {
          label: "ไม่เห็นควร",
          count: inapprop,
          percent: ((inapprop / total) * 100).toFixed(1),
          color: "red darken-4"
        },
        none: {
          label: "ไม่ได้ดำเนินการ",
          count: none,
          percent: ((none / total) * 100).toFixed(1),
          color: "orange"
        }
      };
    },

    getTopN(data, field, n) {
      const counts = {};
      data.forEach(item => {
        const val = item[field];
        if (val) counts[val] = (counts[val] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, n);
    },

    // ==========================================
    // UI HELPERS
    // ==========================================
    getDocColor(status) {
      if (status === "complete") return "green";
      if (status === "incomplete") return "orange";
      if (status === "do_not") return "red";
      return "grey";
    },
    getDocLabel(status) {
      if (status === "complete") return "สมบูรณ์";
      if (status === "incomplete") return "ไม่สมบูรณ์";
      if (status === "do_not") return "ไม่จัดส่ง";
      return "-";
    },
    getOpinionColor(val) {
      if (val === "approp") return "green";
      if (val === "inapprop") return "red darken-4";
      return "orange";
    },
    getOpinionLabel(val) {
      if (val === "approp") return "เห็นควร";
      if (val === "inapprop") return "ไม่เห็นควร";
      return "ไม่ดำเนินการ";
    },
    getStatusColor(status) {
      if (status === "transfer") return "orange darken-1";
      if (status === "agree" || status === "demand") return "success";
      if (status === "sw_normal" || status === "sw_agree") return "cyan";
      return "grey";
    },
    getStatusText(status) {
      const map = {
        demand: "ปกติ",
        agree: "ปกติ",
        transfer: "ตัดโอน",
        sw_normal: "สับเปลี่ยน",
        sw_agree: "แลกเปลี่ยน"
      };
      return map[status] || status || "-";
    },

    // ==========================================
    // EXPORT EXCEL (ใช้ XLSX + file-saver เหมือน ConditionsTransferSuccess)
    // ==========================================
    exportToExcel() {
      const exportData = this.allData.map((item, idx) => ({
        ลำดับ: idx + 1,
        ตรวจสอบ: this.hasIssue(item) ? "ต้องตรวจสอบ" : "ปกติ",
        รูปแบบการย้าย: this.getStatusText(item.ctsstatus_select),
        "คำนำหน้า": item.title_s || "",
        "ชื่อ": item.frist_name || "",
        "นามสกุล": item.last_name || "",
        รหัสบัตรประชาชน: item.ctsid_card || "",
        สังกัดเดิม: item.college_name_old || "",
        สังกัดใหม่: item.college_name_new || "",
        รหัสสาขา: item.ctsid_branch || "",
        สาขาวิชา: item.branch_name || "",
        เลขที่ตำแหน่งใหม่: item.ctsid_position || "",
        เลขที่ตำแหน่งเดิม: item.ctsid_postion_old || "",
        "คะแนน สอจ.": item.tpvecprovince_score || 0,
        "คะแนน กก.": item.score_1420 || 0,
        คะแนนรวม: this.calcTotalScore(item).toFixed(2),
        สถานะเอกสาร: this.getDocLabel(item.status_document),
        "ความเห็น ผอ.": this.getOpinionLabel(item.comment_dr_c),
        "ความเห็น ผอ.สถบ.": this.getOpinionLabel(item.tp_comment_dr_stb),
        ความเห็นกรรมการ: this.getOpinionLabel(item.Mcommittee),
        ช่วยราชการ:
          item.service_statuss === "not_service" ? "ปกติ" : "ช่วยราชการ",
        เหตุผล: item.reason || "",
        อายุงาน: item.age_app_time || "",
        อายุราชการ: item.age_time || "",
        หมายเหตุ: this.getRemarkObj(item).text
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto column widths
      if (exportData.length > 0) {
        const colWidths = Object.keys(exportData[0]).map(key => ({
          wch: Math.max(
            key.length * 2,
            ...exportData.map(row => String(row[key] || "").length)
          )
        }));
        worksheet["!cols"] = colWidths;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "รายงานสรุปการย้าย");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
      const data = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
      });
      saveAs(
        data,
        "รายงานสรุปการย้าย_" +
          (this.periods.period_times || "") +
          "_" +
          (this.periods.period_year || "") +
          ".xlsx"
      );
      this.showSnackbar("success", "Export เรียบร้อย");
    },

    // ==========================================
    // SNACKBAR
    // ==========================================
    showSnackbar(color, text) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.icon =
        color === "success"
          ? "mdi-check-circle"
          : color === "warning"
          ? "mdi-alert"
          : "mdi-alert-circle";
      this.snackbar.show = true;
    }
  },

  props: {
    source: String
  }
};
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}

.validation-card-error {
  border-top: 3px solid #f44336;
}
.validation-card-ok {
  border-top: 3px solid #2e7d32;
}

.error-row {
  background: linear-gradient(
    90deg,
    rgba(244, 67, 54, 0.05) 0%,
    rgba(244, 67, 54, 0.01) 100%
  ) !important;
  border-left: 3px solid #f44336;
}

.warning-row {
  background: linear-gradient(
    90deg,
    rgba(255, 152, 0, 0.05) 0%,
    rgba(255, 152, 0, 0.01) 100%
  ) !important;
  border-left: 3px solid #A86400;
}

@media print {
  .v-navigation-drawer,
  .v-toolbar,
  .v-system-bar {
    display: none !important;
  }
  .stat-card {
    break-inside: avoid;
  }
  .v-card {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #ccc !important;
  }
}
</style>
